import {Alert, Button, Group, SegmentedControl, Select, Stack, Text} from '@mantine/core'
import type {UseFormReturnType} from '@mantine/form'

import {CustomerPocSelector} from 'src/customers/customer-poc-selector'
import type {DispatchYard} from 'src/dispatch/schemas'
import {CustomerAutocomplete} from 'src/jobs/customer-autocomplete'
import {customerDataLayer} from 'src/jobs/customer-data-layer'
import {formatJobDate} from 'src/jobs/job-schedule-format'
import type {CreateJobFormValues} from 'src/jobs/types'
import {useRecentJobsByCustomer} from 'src/jobs/use-jobs'
import {isRealSite} from 'src/lib/texas-bounds'

import type {AddToOrder} from './job-entry-logic'
import {OpenOrderNotice} from './open-order-notice'
import {SiteCards} from './site-cards'

const RECENT_JOB_LIMIT = 20

type CustomerSiteSectionProps = {
    form: UseFormReturnType<CreateJobFormValues>
    values: CreateJobFormValues
    errors: Record<string, string>
    customerName: string | null
    order: AddToOrder | null
    fromQuote: boolean
    yards: DispatchYard[]
    siteYardId: string | null
    onCustomerChange: (customerId: string | null, name: string | null) => void
    onStartFromJob: (jobId: string | null) => void
}

export function CustomerSiteSection({
    form,
    values,
    errors,
    customerName,
    order,
    fromQuote,
    yards,
    siteYardId,
    onCustomerChange,
    onStartFromJob,
}: CustomerSiteSectionProps) {
    const {data: recentJobs = []} = useRecentJobsByCustomer(values.customerId)
    const knownCustomer = values.customerId
        ? customerDataLayer.getCustomerById(values.customerId)
        : undefined
    const notRealSites = values.locations.filter(
        (location) =>
            location.latitude != null &&
            location.longitude != null &&
            !isRealSite(location.latitude, location.longitude),
    )
    const yardHint = values.yardPickedByRep
        ? 'Yard picked for this job.'
        : siteYardId
          ? 'Closest yard to the site.'
          : values.dispatchYardId
            ? 'Your yard until a site is picked.'
            : 'Closest yard to the site, once picked.'

    return (
        <>
            {order ? (
                <Text fw={500}>{order.customerName}</Text>
            ) : values.customerId && !knownCustomer && customerName ? (
                <Group justify="space-between">
                    <Text fw={500}>{customerName}</Text>
                    {fromQuote ? null : (
                        <Button
                            variant="subtle"
                            size="compact-sm"
                            onClick={() => onCustomerChange(null, null)}
                        >
                            Change customer
                        </Button>
                    )}
                </Group>
            ) : (
                <CustomerAutocomplete
                    value={values.customerId}
                    error={errors['customerId']}
                    dataLayer={customerDataLayer}
                    onChange={onCustomerChange}
                />
            )}
            {values.customerId && !order ? (
                <Select
                    label="Start from an old job (optional)"
                    description="Copies work, crew, equipment, rates, sites and notes."
                    placeholder="Start from scratch"
                    clearable
                    searchable
                    data={recentJobs.slice(0, RECENT_JOB_LIMIT).map((job) => ({
                        value: job.id,
                        label: `${job.displayId} ${job.name} (${formatJobDate(job.startDate)})`,
                    }))}
                    value={values.cloneJobId}
                    onChange={onStartFromJob}
                />
            ) : null}
            <SiteCards
                customerId={values.customerId}
                locations={values.locations}
                error={errors['locations']}
                onChange={(locations) => form.setFieldValue('locations', locations)}
            />
            {notRealSites.length > 0 ? (
                <Alert color="yellow" variant="light" data-testid="site-not-real">
                    Invalid map position for{' '}
                    {notRealSites.map((location) => location.name).join(', ')}. Fix it to save.
                </Alert>
            ) : null}
            {order ? null : (
                <OpenOrderNotice customerId={values.customerId} locations={values.locations} />
            )}
            <CustomerPocSelector
                customerId={values.customerId}
                value={values.customerContactId}
                onChange={(contactId) => form.setFieldValue('customerContactId', contactId)}
            />
            {order?.dispatchYardId ? (
                <Text size="sm">
                    Dispatch from:{' '}
                    {yards.find((yard) => yard.id === order.dispatchYardId)?.name ??
                        "the order's yard"}{' '}
                    <Text span size="xs" c="dimmed">
                        (set by the order)
                    </Text>
                </Text>
            ) : (
                <Stack gap={4}>
                    <SegmentedControl
                        aria-label="Dispatch from"
                        value={values.dispatchYardId ?? ''}
                        data={yards.map((yard) => ({value: yard.id, label: yard.name}))}
                        onChange={(yardId) =>
                            form.setValues({dispatchYardId: yardId, yardPickedByRep: true})
                        }
                        style={{alignSelf: 'flex-start'}}
                    />
                    <Text size="xs" c={errors['dispatchYardId'] ? 'red' : 'dimmed'}>
                        {errors['dispatchYardId'] ?? yardHint}
                    </Text>
                </Stack>
            )}
        </>
    )
}
